import type { GenerationJobRow } from "@/types/workspace"

/**
 * Оценочный прогресс генерации — чистая косметика на клиенте. Провайдер (Seedance и др.)
 * процентов не отдаёт, и полоса стояла бы на проценте «Генерация» весь ролик.
 * Пока задача в фазе генерации, полоса линейно идёт от серверного процента к цели
 * за ESTIMATED_GENERATION_MS и замирает на цели, если генерация дольше.
 *
 * Состояние фазы лежит в localStorage (по записи на задачу): после обновления страницы
 * полоса продолжает с того же места, а не откатывается к серверному проценту.
 */

export type EstimatedProgressJob = Pick<
  GenerationJobRow,
  "id" | "job_kind" | "status" | "progress" | "status_detail" | "shot_ids"
>

export type PhaseStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">

/** Ориентир длительности одной генерации видео. */
export const ESTIMATED_GENERATION_MS = 7 * 60 * 1000
export const ESTIMATED_PROGRESS_STORAGE_KEY = "it2tv:job-estimated-progress:v1"
/** Запись, которую давно не обновляли (задача удалена, вкладка закрыта), выбрасываем. */
const STORAGE_MAX_AGE_MS = 24 * 60 * 60 * 1000

const GENERATION_DETAILS = new Set(["awaiting", "provider queued", "provider running"])
/** Эти задачи сами шлют реальный процент в фазе awaiting — не подменяем. */
const REAL_PROGRESS_KINDS = new Set(["topaz_post_production", "shot_timeline_lipsync"])
/** Доля полосы шота, до которой доходит оценка: 5 + 0.89 × 90 ≈ 85 % у одиночного шота. */
const SHOT_BAND_SHARE = 0.89

type Phase = { base: number; startedAt: number; generating: boolean; shown: number; seenAt: number }

const phases = new Map<number, Phase>()

const clampPercent = (value: number) => Math.min(100, Math.max(0, Math.round(value || 0)))

function browserStorage(): PhaseStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage
  } catch {
    return null
  }
}

function sanitizePhase(raw: unknown): Phase | null {
  if (!raw || typeof raw !== "object") return null
  const p = raw as Record<string, unknown>
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null)
  const base = num(p.base)
  const startedAt = num(p.startedAt)
  const shown = num(p.shown)
  const seenAt = num(p.seenAt)
  if (base === null || startedAt === null || shown === null || seenAt === null) return null
  return { base, startedAt, generating: p.generating === true, shown: clampPercent(shown), seenAt }
}

function readPhases(storage: PhaseStorage): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(ESTIMATED_PROGRESS_STORAGE_KEY) ?? "{}")
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** Пишем одну задачу поверх свежего чтения: соседняя вкладка не теряет свои записи. */
function writePhase(storage: PhaseStorage | null, jobId: number, phase: Phase | null, now: number) {
  if (!storage) return
  try {
    const all = readPhases(storage)
    if (phase) all[jobId] = phase
    else delete all[jobId]
    for (const [key, raw] of Object.entries(all)) {
      const entry = sanitizePhase(raw)
      if (!entry || now - entry.seenAt > STORAGE_MAX_AGE_MS) delete all[key]
    }
    if (Object.keys(all).length === 0) storage.removeItem(ESTIMATED_PROGRESS_STORAGE_KEY)
    else storage.setItem(ESTIMATED_PROGRESS_STORAGE_KEY, JSON.stringify(all))
  } catch {
    // Квота / приватный режим: оценка просто не переживёт перезагрузку.
  }
}

function storedPhase(storage: PhaseStorage | null, jobId: number, now: number): Phase | null {
  if (!storage) return null
  const entry = sanitizePhase(readPhases(storage)[jobId])
  return entry && now - entry.seenAt <= STORAGE_MAX_AGE_MS ? entry : null
}

export function isEstimatedGenerationPhase(job: EstimatedProgressJob): boolean {
  return (
    job.status === "processing" &&
    GENERATION_DETAILS.has(job.status_detail ?? "") &&
    !REAL_PROGRESS_KINDS.has(job.job_kind)
  )
}

/** Цель оценки. Пакет шотов: сервер делит 5…95 % на равные полосы, цель — 89 % полосы текущего шота. */
export function estimatedGenerationTarget(job: EstimatedProgressJob): number {
  const total = Math.max(1, job.shot_ids?.length ?? 1)
  const perShot = 90 / total
  const done = Math.min(total - 1, Math.max(0, Math.floor((clampPercent(job.progress) - 5) / perShot)))
  return Math.round(5 + perShot * (done + SHOT_BAND_SHARE))
}

/**
 * Процент для отображения. Отсчёт фазы — с момента, когда клиент впервые увидел задачу
 * в генерации с этим серверным процентом (переживает перезагрузку через storage).
 * Пока задача processing, значение не убывает.
 */
export function estimatedJobProgress(
  job: EstimatedProgressJob,
  now: number = Date.now(),
  store: Map<number, Phase> = phases,
  storage: PhaseStorage | null = store === phases ? browserStorage() : null,
): number {
  const actual = clampPercent(job.progress)
  if (job.status !== "processing") {
    if (store.delete(job.id)) writePhase(storage, job.id, null, now)
    return actual
  }

  let previous = store.get(job.id)
  if (!previous) {
    previous = storedPhase(storage, job.id, now) ?? undefined
    if (previous) store.set(job.id, previous)
  }

  let value = actual
  let startedAt = now
  const generating = isEstimatedGenerationPhase(job)
  if (generating) {
    if (previous?.generating && previous.base === actual) startedAt = previous.startedAt
    const target = estimatedGenerationTarget(job)
    if (target > actual) {
      const share = Math.min(1, Math.max(0, now - startedAt) / ESTIMATED_GENERATION_MS)
      value = Math.floor(actual + (target - actual) * share)
    }
  }

  const shown = Math.max(value, previous?.shown ?? 0)
  const changed =
    !previous ||
    previous.base !== actual ||
    previous.startedAt !== startedAt ||
    previous.generating !== generating ||
    previous.shown !== shown
  const next = { base: actual, startedAt, generating, shown, seenAt: changed ? now : (previous?.seenAt ?? now) }
  store.set(job.id, next)
  if (changed) writePhase(storage, job.id, next, now)
  return shown
}
